import React from 'react';
import { Card, Stack, useTheme } from '@fti/design-system';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: '16px' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 8px 0', color: theme.colors.text.primary, fontFamily: "'Source Sans 3', sans-serif" }}>{title}</h3>
      <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, lineHeight: 1.6, fontFamily: "'Source Sans 3', sans-serif" }}>
        {children}
      </div>
    </div>
  );
};

export const InstructionsPanel: React.FC = () => {
  const { theme } = useTheme();
  return (
    <Card>
      <Card.Body>
        <Stack gap="md">
          <h2 style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0, color: theme.colors.text.primary, fontFamily: "'Source Sans 3', sans-serif" }}>
            Graph Instructions
          </h2>

          <Section title="Navigation">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Pan</strong> — Click and drag on empty space to move the view.</li>
              <li><strong>Zoom</strong> — Scroll wheel to zoom in/out.</li>
              <li><strong>Select node/edge</strong> — Click a node or edge to see its details in the panel on the right.</li>
            </ul>
          </Section>

          <Section title="Multi-Select">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Shift+Click</strong> — Hold Shift and click multiple nodes to select them.</li>
              <li><strong>Clear selection</strong> — Click on empty space (without Shift) to deselect all.</li>
              <li>When 2 or more nodes are selected, the Export button changes to <strong>"Export highlighted nodes"</strong> and will export only the selected subgraph.</li>
            </ul>
          </Section>

          <Section title="Layout Controls">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Layout dropdown</strong> — Choose from Force (Bilkent), Force (CoSE), Circle, Concentric, Hierarchical, or Grid.</li>
              <li><strong>Repulsion slider</strong> — Visible for force layouts. Higher values spread nodes further apart.</li>
              <li><strong>Play button</strong> — Re-runs the selected layout with current settings.</li>
              <li><strong>Fullscreen</strong> — Expands the graph to fill the browser window. Press Escape or click "Exit" to return.</li>
            </ul>
          </Section>

          <Section title="Search">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Type a node name in the <strong>Search nodes</strong> box and press <strong>Enter</strong>.</li>
              <li>Matching nodes are highlighted with an orange border and the view zooms to fit them.</li>
              <li>Clear the search box to remove highlights.</li>
            </ul>
          </Section>

          <Section title="Color By">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Entity Type</strong> — Colors nodes by their label (Person, Company, Address, etc.).</li>
              <li><strong>Jurisdiction</strong> — Colors nodes by their jurisdiction property.</li>
              <li>The legend in the bottom-left shows the color mapping.</li>
            </ul>
          </Section>

          <Section title="Export">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Interactive HTML</strong> — Standalone HTML file with full interactivity (layouts, filters, search).</li>
              <li><strong>SVG Image</strong> — Vector image, scalable for print.</li>
              <li><strong>PNG Image</strong> — Raster image for presentations.</li>
              <li><strong>Gephi (GEXF)</strong> — For analysis in Gephi.</li>
              <li><strong>NetworkX (PKL)</strong> — Python pickle for programmatic analysis.</li>
            </ul>
          </Section>

          <Section title="Cypher Console">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Switch to the <strong>Cypher Console</strong> tab for advanced graph queries.</li>
              <li>Use templates (Neighbors, Shortest Path, Subgraph by Label) or write custom Cypher.</li>
              <li>Query results highlight matching nodes on the graph.</li>
            </ul>
          </Section>

          <Section title="Adding Data">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Add Node</strong> — Create individual entities (Person, Company, etc.) with properties.</li>
              <li><strong>Add Edge</strong> — Create relationships between existing nodes.</li>
              <li><strong>Upload</strong> — Bulk upload nodes and edges from an Excel file.</li>
            </ul>
          </Section>
        </Stack>
      </Card.Body>
    </Card>
  );
};
